interface NormalisedAddressComponent {
	types: string[];
	name: string;
	shortName?: string;
}

function parseNormalisedAddressComponents(addressComponents: NormalisedAddressComponent[]): Record<string, string> {
	const relevantAddressComponents: Record<string, string> = {};

	for (const component of addressComponents) {
		const { types, name, shortName } = component;

		for (const type of types) {
			if (type === "street_number") {
				relevantAddressComponents.streetNumber = name;
			} else if (type === "route") {
				relevantAddressComponents.route = name;
			} else if (type === "locality") {
				relevantAddressComponents.locality = name;
			} else if (type === "sublocality_level_1") {
				relevantAddressComponents.sublocalityLevel1 = name;
			} else if (type === "sublocality") {
				// A sublocality_level_N component also carries the plain
				// "sublocality" type; keep the first (most specific) one seen.
				relevantAddressComponents.sublocality ||= name;
			} else if (type === "neighborhood") {
				relevantAddressComponents.neighborhood = name;
			} else if (type === "administrative_area_level_1") {
				// Prefer the short form ("VIC"); degrade to the long form rather
				// than aborting the whole address parse on one malformed field.
				relevantAddressComponents.state = shortName || name;
			} else if (type === "administrative_area_level_2") {
				// AU: the council/LGA (e.g. "City of Melbourne"). Kept as its own
				// field only — never a substitute for the suburb.
				relevantAddressComponents.region = name;
			} else if (type === "country") {
				relevantAddressComponents.country = name;
			} else if (type === "postal_code") {
				relevantAddressComponents.postalCode = name;
			}
		}
	}

	return relevantAddressComponents;
}

export function parseGooglePlacesAddressComponents(addressComponents: google.maps.places.AddressComponent[] | undefined): Record<string, string> {
	return parseNormalisedAddressComponents(
		(addressComponents ?? [])
			.map((component) => ({
				types: component.types,
				name: component.longText || component.shortText || "",
				shortName: component.shortText || undefined,
			}))
			.filter((component) => component.name),
	);
}

/**
 * Parses Google Geocoder address components (long_name/short_name format)
 * into a flat record of relevant address fields.
 * Mirrors parseGooglePlacesAddressComponents but for the Geocoder API format.
 */
export function parseGeocoderAddressComponents(addressComponents: google.maps.GeocoderAddressComponent[]): Record<string, string> {
	return parseNormalisedAddressComponents(
		(addressComponents ?? [])
			.map((component) => ({
				types: component.types,
				name: component.long_name || component.short_name || "",
				shortName: component.short_name || undefined,
			}))
			.filter((component) => component.name),
	);
}

/**
 * Derives the waypoint suburb from parsed address components.
 *
 * AU: Google returns the suburb as `locality` for ordinary street addresses,
 * but for airports, hotels, POIs and some unit/subpremise addresses it arrives
 * as a sublocality or neighborhood instead. `region`
 * (administrative_area_level_2) is the council/LGA — never a suburb — so it is
 * deliberately not a fallback here; when no component qualifies this returns
 * an empty string rather than a wrong value.
 *
 * NZ: the mapping is inverted — Google returns the CITY as `locality`
 * ("Auckland") and the actual suburb as `sublocality_level_1` ("Māngere",
 * "Albany", "Half Moon Bay"). So for New Zealand addresses the sublocality is
 * preferred and `locality` becomes the last resort. Detected via the `country`
 * component, which Google returns as the long name "New Zealand".
 */
export function componentsToWaypointSuburb(components: Record<string, string>): string {
	// Country arrives as long name "New Zealand" or short code "NZ" depending on
	// which Google API/field populated it — accept either.
	const country = components.country;
	if (country === "New Zealand" || country === "NZ") {
		return components.sublocalityLevel1 || components.sublocality || components.neighborhood || components.locality || "";
	}
	return components.locality || components.sublocalityLevel1 || components.sublocality || components.neighborhood || "";
}
